// D50.1 — Workspace Brand Text Rules editor (Settings > Brand Rules tab).
// Used by D51 video AI review to flag any deviation from the expected casing
// of brand terms (e.g. "DICH" must be all-caps, "Stellar Productions" must
// keep S+P capitalized).
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Trash2, Plus, Loader2, ShieldCheck, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_OPTIONS = ['high', 'medium', 'low'];
const MATCH_OPTIONS = ['word', 'substring'];

export default function BrandTextRulesPanel({ workspaceId }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchRules = async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/brand-text-rules`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('stellar_token')}` },
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setRules(data.rules || []);
    } catch (e) {
      toast.error(`Failed to load brand rules: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); /* eslint-disable-next-line */ }, [workspaceId]);

  const updateRule = (idx, patch) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRule = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      { term: '', expected: '', severity: 'medium', match: 'word' },
    ]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = rules
        .filter((r) => (r.term || '').trim())
        .map((r) => ({
          id: r.id,
          term: (r.term || '').trim(),
          expected: (r.expected || r.term || '').trim(),
          severity: r.severity || 'medium',
          match: r.match || 'word',
        }));
      const r = await fetch(`${API}/api/workspaces/${workspaceId}/brand-text-rules`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('stellar_token')}`,
        },
        body: JSON.stringify({ rules: payload }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      setRules(data.rules || []);
      toast.success(`Saved ${data.count} brand rule${data.count === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card/60 backdrop-blur" data-testid="brand-text-rules-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-gold" /> Brand Text Rules
        </CardTitle>
        <p className="text-[11px] text-muted-foreground/70 mt-1">
          Enforce exact casing for brand terms. AI Video Review (D51) will flag any deviation as a high-severity capitalization issue.
          Example: <span className="font-mono text-foreground/80">DICH</span> must always appear as <span className="font-mono text-foreground/80">DICH</span>;
          <span className="font-mono text-foreground/80"> Stellar Productions</span> must keep S+P capitalized.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && (
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground/70">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        )}

        {!loading && rules.length === 0 && (
          <div
            className="rounded-md border border-dashed border-border/40 px-3 py-6 text-center text-[12px] text-muted-foreground/70"
            data-testid="brand-text-rules-empty"
          >
            No brand rules yet. Add one to start flagging text deviations in video submissions.
          </div>
        )}

        {!loading && rules.map((r, idx) => (
          <div
            key={r.id || idx}
            className="grid grid-cols-12 gap-2 items-end rounded-md border border-border/30 bg-foreground/[0.02] p-2"
            data-testid={`brand-text-rule-${idx}`}
          >
            <div className="col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Term</Label>
              <Input
                value={r.term || ''}
                onChange={(e) => updateRule(idx, { term: e.target.value, expected: r.expected || e.target.value })}
                placeholder="DICH"
                className="text-[12px] h-8"
                data-testid={`brand-text-rule-term-${idx}`}
              />
            </div>
            <div className="col-span-4">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Expected casing</Label>
              <Input
                value={r.expected || ''}
                onChange={(e) => updateRule(idx, { expected: e.target.value })}
                placeholder="DICH"
                className="text-[12px] h-8 font-mono"
                data-testid={`brand-text-rule-expected-${idx}`}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Severity</Label>
              <select
                value={r.severity || 'medium'}
                onChange={(e) => updateRule(idx, { severity: e.target.value })}
                className="w-full h-8 text-[12px] rounded-md border border-input bg-background px-2"
                data-testid={`brand-text-rule-severity-${idx}`}
              >
                {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-span-1">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Match</Label>
              <select
                value={r.match || 'word'}
                onChange={(e) => updateRule(idx, { match: e.target.value })}
                className="w-full h-8 text-[12px] rounded-md border border-input bg-background px-2"
                data-testid={`brand-text-rule-match-${idx}`}
              >
                {MATCH_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="col-span-1 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => removeRule(idx)}
                data-testid={`brand-text-rule-remove-${idx}`}
                title="Remove this rule"
              >
                <Trash2 className="h-3.5 w-3.5 text-red-400" />
              </Button>
            </div>
          </div>
        ))}

        <div className="flex items-center justify-between pt-2 border-t border-border/20">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addRule}
            disabled={loading || rules.length >= 50}
            data-testid="brand-text-rules-add"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add rule
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={save}
            disabled={loading || saving}
            data-testid="brand-text-rules-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Save rules
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
